import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { inviteService } from '@/features/mattermost/services/invite.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

// Schema será criado dinamicamente baseado nas equipes permitidas
const createRegisterSchema = (hasAllowedTeams: boolean) =>
  z.object({
    password: z
      .string()
      .min(8, 'Senha deve ter no mínimo 8 caracteres')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/,
        'Senha deve conter pelo menos uma letra minúscula, uma maiúscula, um número e um caractere especial',
      ),
    teamIds: hasAllowedTeams
      ? z.array(z.string()).min(1, 'Selecione pelo menos uma equipe')
      : z.array(z.string()).optional(),
  });

type RegisterFormData = z.infer<ReturnType<typeof createRegisterSchema>>;

export const Route = createFileRoute('/invite/$code/$tenantId')({
  component: InvitePage,
  beforeLoad: () => {
    // Não redirecionar se já estiver autenticado - permitir acesso à página de convite
    // A página vai lidar com a autenticação se necessário
  },
});

function InvitePage() {
  const { code, tenantId } = Route.useParams();
  const navigate = useNavigate();

  // Validar convite ao carregar
  const {
    data: inviteData,
    isLoading: isLoadingInvite,
    error: inviteError,
  } = useQuery({
    queryKey: ['invite', code, tenantId],
    queryFn: () => inviteService.validateInvite(code, tenantId),
    retry: false,
  });

  // Equipes permitidas vêm do inviteData
  const allowedTeams = inviteData?.allowedTeams || [];
  const hasAllowedTeams = allowedTeams.length > 0;

  const registerSchema = createRegisterSchema(hasAllowedTeams);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      teamIds: [],
    },
  });

  const selectedTeamIds = watch('teamIds') || [];

  const registerMutation = useMutation({
    mutationFn: (data: RegisterFormData) => inviteService.registerInvite(code, tenantId, data),
    onSuccess: () => {
      toast.success('Conta criada com sucesso! Redirecionando para login...');
      setTimeout(() => {
        navigate({
          to: '/login',
          search: {
            message: 'Conta criada com sucesso! Faça login para continuar.',
          },
        });
      }, 1500);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Erro ao criar conta';
      if (error.response?.status === 409) {
        // Email já existe - redirecionar para login
        toast.info('Este email já possui uma conta. Redirecionando para login...');
        setTimeout(() => {
          navigate({
            to: '/login',
            search: {
              message:
                'Este email já possui uma conta. Faça login para vincular sua conta do Mattermost.',
            },
          });
        }, 1500);
      } else {
        toast.error(errorMessage);
      }
    },
  });

  const onSubmit = async (data: RegisterFormData) => {
    registerMutation.mutate(data);
  };

  const toggleTeam = (teamId: string) => {
    const current = selectedTeamIds;
    if (current.includes(teamId)) {
      setValue(
        'teamIds',
        current.filter((id) => id !== teamId),
      );
    } else {
      setValue('teamIds', [...current, teamId]);
    }
  };

  if (isLoadingInvite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page-background">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2">Validando convite...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (inviteError || !inviteData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Convite Inválido</CardTitle>
            <CardDescription>Este convite não é válido ou já foi utilizado.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate({ to: '/login' })} className="w-full">
              Ir para Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-page-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Criar Conta</CardTitle>
          <CardDescription>
            Complete seu cadastro no Asseto usando o convite do Mattermost
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={inviteData.email}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                Email vinculado à sua conta do Mattermost (não editável)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                {...register('password')}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Mínimo 8 caracteres, com maiúscula, minúscula, número e caractere especial
              </p>
            </div>

            {allowedTeams.length > 0 && (
              <div className="space-y-2">
                <Label>
                  Equipes <span className="text-destructive">*</span>
                </Label>
                <div className="space-y-2 border rounded-md p-3 max-h-60 overflow-y-auto">
                  {allowedTeams.map((team) => (
                    <div key={team.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`team-${team.id}`}
                        checked={selectedTeamIds.includes(team.id)}
                        onCheckedChange={() => toggleTeam(team.id)}
                      />
                      <Label
                        htmlFor={`team-${team.id}`}
                        className="text-sm font-normal cursor-pointer flex-1"
                      >
                        {team.name}
                        {team.description && (
                          <span className="text-xs text-muted-foreground ml-2">
                            - {team.description}
                          </span>
                        )}
                      </Label>
                    </div>
                  ))}
                </div>
                {errors.teamIds && (
                  <p className="text-sm text-destructive">{errors.teamIds.message}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Selecione pelo menos uma equipe para participar
                </p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting || registerMutation.isPending}
            >
              {isSubmitting || registerMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Criando conta...
                </>
              ) : (
                'Criar Conta'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
